import { fmtNum } from '../ui/ListFoot';
import type { Audience, GenreOption, Literary } from '../book/genres';
import { booksChanged } from '../lib/changed';
import { supabase } from '../lib/supabase';

// Add a book (the + in the header; agreed with Kristi Oct 28; decisions doc › "From the build:
// Add a book"). The rules here have no screen, so the logic tests can check them.

export type EditionKind = 'print' | 'ebook' | 'audiobook' | 'other';

/** One book the search found (book-lookup's search mode; supabase/functions/_shared/lookup/search.ts). */
export interface Found {
  key: string;
  source: 'Open Library' | 'Google Books';
  title: string;
  subtitle: string | null;
  authors: string[];
  year: number | null;
  coverUrl: string | null;
  kind: EditionKind | null;
  url: string;
  openLibraryId: string | null;
  googleBooksId: string | null;
  summary: string | null;
  summarySource: string | null;
  isbn13: string | null;
  pages: number | null;
  publisher: string | null;
  language: string | null;
  editions: number | null;
  /** The book's subjects clearly say fiction (Open Library or Google). */
  fiction?: boolean;
}

export interface SearchAnswer {
  status: 'found' | 'not_found' | 'bad_request';
  read: { words: string; isbn: string; asin: string; audibleId: string; goodreadsId: string; kind: EditionKind | null };
  results: Found[];
  message: string | null;
}

/** The copy choices, in order (Kristi, Oct 28). */
export type CopyKey = 'kindle' | 'audible' | 'paper' | 'library' | 'other' | 'none';
export const COPY_CHOICES: { key: CopyKey; label: string }[] = [
  { key: 'kindle', label: 'Kindle' },
  { key: 'audible', label: 'Audible' },
  { key: 'paper', label: 'Paper' },
  { key: 'library', label: 'Library' },
  { key: 'other', label: 'Other' },
  { key: 'none', label: 'No copy yet' },
];

/** Where a new book can go: always a shelf, Future reads first (Kristi, Oct 28: no "No shelf yet" here). */
export type AddShelf = 'up_next' | 'future_reads' | 'reference' | 'currently_reading' | 'finished' | 'not_for_me';
export const SHELF_CHOICES: { key: AddShelf; label: string }[] = [
  { key: 'up_next', label: 'Up Next' },
  { key: 'future_reads', label: 'Future reads' },
  { key: 'reference', label: 'Reference' },
  { key: 'currently_reading', label: 'Start reading now' },
];
/** More choices ▸ (Kristi, Oct 28): you've read it, or decided not to. */
export const MORE_CHOICES: { key: AddShelf; label: string }[] = [
  { key: 'finished', label: 'Finished…' },
  { key: 'not_for_me', label: 'Not for me…' },
];
export const DEFAULT_SHELF: AddShelf = 'future_reads';
/** Shelves where a place can be chosen (Reference has no order; Start reading goes to the top). */
export const ordered = (s: AddShelf): s is 'up_next' | 'future_reads' => s === 'up_next' || s === 'future_reads';
export const shelfName = (s: AddShelf) =>
  s === 'currently_reading' ? 'Currently Reading' : s === 'finished' ? 'Finished' : s === 'not_for_me' ? 'Not for me' : SHELF_CHOICES.find((x) => x.key === s)!.label;
/** The main button names where the book goes ("Add to Future reads"; Kristi, Oct 28). */
export const addButton = (s: AddShelf) =>
  s === 'currently_reading' ? 'Add and start reading' : s === 'finished' ? 'Add as finished' : `Add to ${shelfName(s)}`;

/** The copy pre-picked from the match: audiobook → Audible, ebook → Kindle, print → Paper; when the match
 * doesn't say, No copy yet (Kristi, Nov 1: was Kindle). */
export function defaultCopy(kind: EditionKind | null): CopyKey {
  if (kind === 'audiobook') return 'audible';
  if (kind === 'print') return 'paper';
  if (kind === 'ebook') return 'kindle';
  return 'none';
}

const copyKind = (c: CopyKey, libraryListen: boolean): EditionKind | null =>
  c === 'audible' || (c === 'library' && libraryListen) ? 'audiobook' : c === 'paper' ? 'print' : c === 'kindle' || c === 'library' ? 'ebook' : c === 'other' ? 'other' : null;

/**
 * Which kind of printing the search's facts describe: what the search said, else the copy you
 * have (so its cover and length sit on the right edition), else print.
 */
export function editionKind(found: Found | null, copy: CopyKey, libraryListen = false): EditionKind {
  return found?.kind ?? copyKind(copy, libraryListen) ?? 'print';
}

export interface CopyDetails {
  how: 'bought' | 'gift' | 'shared' | 'free';
  store: string;
  /** A gift's giver or a free copy's place (0047); never shared. */
  from?: string;
  price: string;
  foundThrough: string;
  /** Library only: borrowed now or a hold placed. */
  loanStep: 'on_hold' | 'borrowed';
  libraryListen: boolean;
  loanDays: number;
}
export const DEFAULT_COPY_DETAILS: CopyDetails = { how: 'bought', store: '', price: '', foundThrough: '', loanStep: 'on_hold', libraryListen: false, loanDays: 21 };
export const storeFor = (c: CopyKey) => (c === 'kindle' ? 'Amazon' : c === 'audible' ? 'Audible' : '');

/** The copy as Add a copy… takes it (copy_add's details), or null for No copy yet. */
export function copyFor(copy: CopyKey, d: CopyDetails): Record<string, unknown> | null {
  if (copy === 'none') return null;
  if (copy === 'library') {
    return { how: 'library_loan', format: d.libraryListen ? 'libby' : 'kindle', listen: d.libraryListen, loan: { step: d.loanStep, loan_days: d.loanDays } };
  }
  const out: Record<string, unknown> = { how: d.how, format: copy, listen: copy === 'audible' };
  const store = (d.store || storeFor(copy)).trim();
  if (d.how === 'bought' && store) out.store = store;
  if (d.price.trim()) out.price = d.price.trim();
  if (d.foundThrough.trim()) out.found_through = d.foundThrough.trim();
  if ((d.how === 'gift' || d.how === 'free') && d.from?.trim()) out.got_from = d.from.trim();
  return out;
}

export interface Choices {
  /** Required (Kristi, Oct 28): every book has a literary genre. */
  literaryGenre: Literary | null;
  audience: Audience | null;
  genres: string[];
  /** Finished: when (a date with its precision, or your words), stars, ↻ Read again. */
  finished?: { ended: { on: string | null; precision: string | null; words: string | null } | null; rating: number | null; readAgain: boolean };
  /** Not for me (Passed on): reason chips and your words. */
  notForMe?: { chips: string[]; reason: string };
  copy: CopyKey;
  copyDetails: CopyDetails;
  shelf: AddShelf;
  place: { place: string };
  interest: number | null;
  why: string;
  recommendedBy: string;
  /** From a pasted Goodreads, Amazon or Audible link. */
  goodreadsId?: string;
  asin?: string;
  audibleId?: string;
}

/** What book_add (0034) is sent: the found book's facts (or the typed title and authors) and your choices. */
export function addDetails(found: Found | null, manual: { title: string; authors: string[] } | null, c: Choices): Record<string, unknown> {
  const d: Record<string, unknown> = { shelf: c.shelf };
  if (c.literaryGenre) d.literary_genre = c.literaryGenre;
  if (c.audience) d.audience = c.audience;
  if (c.genres.length) d.genres = c.genres;
  if (found) {
    d.title = found.title;
    if (found.subtitle) d.subtitle = found.subtitle;
    d.authors = found.authors;
    if (found.year) d.first_year = found.year;
    if (found.summary) {
      d.summary = found.summary;
      d.summary_source = found.summarySource;
    }
    if (found.openLibraryId) d.open_library_id = found.openLibraryId;
    if (found.googleBooksId) d.google_books_id = found.googleBooksId;
    const kind = editionKind(found, c.copy, c.copyDetails.libraryListen);
    const e: Record<string, unknown> = { kind };
    if (found.coverUrl?.startsWith('https://')) e.cover_url = found.coverUrl;
    if (found.pages && kind !== 'audiobook') e.pages = found.pages;
    if (found.isbn13 && kind === 'print') e.isbn13 = found.isbn13;
    if (found.publisher && found.kind) e.publisher = found.publisher;
    if (found.language && /^[a-z]{2,3}$/.test(found.language)) e.language = found.language;
    if (c.asin && kind === 'ebook') e.asin = c.asin;
    if (c.audibleId && kind === 'audiobook') e.audible_id = c.audibleId;
    d.edition = e;
  } else if (manual) {
    d.title = manual.title.trim();
    d.authors = manual.authors.map((a) => a.trim()).filter(Boolean);
    const kind = editionKind(null, c.copy, c.copyDetails.libraryListen);
    if (c.asin && kind === 'ebook') d.edition = { kind, asin: c.asin };
    if (c.audibleId && kind === 'audiobook') d.edition = { kind, audible_id: c.audibleId };
  }
  if (c.goodreadsId) d.goodreads_id = c.goodreadsId;
  if (ordered(c.shelf)) d.place = c.place;
  const copy = copyFor(c.copy, c.copyDetails);
  if (copy) d.copy = copy;
  if (c.shelf === 'finished' && c.finished) {
    const f: Record<string, unknown> = {};
    const e = c.finished.ended;
    if (e && (e.on || e.words?.trim())) f.ended = { on: e.on, precision: e.precision, words: e.words?.trim() || null };
    if (c.finished.rating) f.rating = c.finished.rating;
    if (c.finished.readAgain) f.read_again = true;
    d.finished = f;
  }
  if (c.shelf === 'not_for_me' && c.notForMe) {
    d.not_for_me = { chips: c.notForMe.chips, ...(c.notForMe.reason.trim() ? { reason: c.notForMe.reason.trim() } : {}) };
  }
  if (c.interest) d.interest = c.interest;
  if (c.why.trim()) d.why_read = c.why.trim();
  if (c.recommendedBy.trim()) d.recommended_by = c.recommendedBy.trim();
  return d;
}

/** Literary genre pre-picked from the match: Fiction when its subjects clearly say so; otherwise you pick. */
export const defaultLiterary = (f: Found | null): Literary | null => (f?.fiction ? 'fiction' : null);

/** Your genres for the picker: name, short name, the literary genre each belongs to, hidden or not. */
export async function loadGenres(): Promise<GenreOption[]> {
  if (!supabase) return [];
  const { data } = await supabase.from('genres').select('name, short_name, sort_order, hidden, starter:starter_genres(fiction)').order('sort_order');
  type G = { name: string; short_name: string | null; hidden: boolean; starter: { fiction: Literary } | { fiction: Literary }[] | null };
  return ((data ?? []) as G[]).map((g) => ({
    name: g.name,
    short: g.short_name,
    hidden: g.hidden,
    literary: (Array.isArray(g.starter) ? g.starter[0]?.fiction : g.starter?.fiction) ?? null,
  }));
}

/** "Nell Newauthor, Ada Already" → the two names; "A and B" too. */
export const splitAuthors = (s: string) =>
  s.split(/\s*(?:,|;|&|\band\b)\s*/i).map((x) => x.trim()).filter(Boolean);

/** A found book's one-line description: authors · year · kind. */
export function foundLine(f: Found): string {
  const kind = f.kind === 'audiobook' ? 'audiobook' : f.kind === 'ebook' ? 'ebook' : f.kind === 'print' ? 'print' : '';
  return [f.authors.slice(0, 2).join(', ') + (f.authors.length > 2 ? ' and others' : ''), f.year ? String(f.year) : '', f.pages && f.kind !== 'audiobook' ? `${f.pages} pages` : '', kind]
    .filter(Boolean)
    .join(' · ');
}

/** "Bottom is #2,105, after Kindred" (or "between X and Y" for a number). */
export function placeLine(place: string, shelf: AddShelf, edge: ShelfEdge | null): string {
  if (!edge || !ordered(shelf)) return '';
  const n = fmtNum;
  if (place === 'top') return edge.count ? `Top is #1, before ${edge.first}` : `The first book on ${shelfName(shelf)}`;
  if (/^\d+$/.test(place)) {
    const k = Math.min(Number(place), edge.count + 1);
    if (k <= 1) return edge.count ? `#1, before ${edge.first}` : `The first book on ${shelfName(shelf)}`;
    if (k > edge.count) return `#${n(edge.count + 1)}, the bottom, after ${edge.last}`;
    return edge.between ? `#${n(k)}, between ${edge.between[0]} and ${edge.between[1]}` : `#${n(k)}`;
  }
  return edge.count ? `Bottom is #${n(edge.count + 1)}, after ${edge.last}` : `The first book on ${shelfName(shelf)}`;
}

export interface ShelfEdge {
  count: number;
  first: string | null;
  last: string | null;
  /** The two books around place n, when one was asked for. */
  between?: [string, string];
}

type Row = Record<string, unknown>;
const titleOf = (r: Row | undefined) => {
  const b = r?.book as Row | undefined;
  const w = (Array.isArray(b?.work) ? b?.work[0] : b?.work) as Row | undefined;
  return (w?.title as string) ?? null;
};

/** How many books a shelf holds, its first and last, and the two around place n. */
export async function shelfEdge(shelf: 'up_next' | 'future_reads', n?: number): Promise<ShelfEdge | null> {
  const db = supabase;
  if (!db) return null;
  const pick = 'position, book:books!inner(status, removed_at, work:works!inner(title))';
  const base = () => db.from('book_positions').select(pick).eq('shelf', shelf).eq('book.status', shelf).is('book.removed_at', null);
  const [count, first, last, around] = await Promise.all([
    db.from('book_positions').select('id, book:books!inner(status, removed_at)', { count: 'exact', head: true }).eq('shelf', shelf).eq('book.status', shelf).is('book.removed_at', null),
    base().order('position', { ascending: true }).limit(1),
    base().order('position', { ascending: false }).limit(1),
    n && n > 1 ? base().order('position', { ascending: true }).range(n - 2, n - 1) : Promise.resolve({ data: null }),
  ]);
  if (count.error) return null;
  const a = (around.data ?? []) as Row[];
  return {
    count: count.count ?? 0,
    first: titleOf((first.data as Row[] | null)?.[0]),
    last: titleOf((last.data as Row[] | null)?.[0]),
    between: a.length === 2 && titleOf(a[0]) && titleOf(a[1]) ? [titleOf(a[0])!, titleOf(a[1])!] : undefined,
  };
}

/** Asks book-lookup's search mode. */
export async function searchFor(text: string): Promise<SearchAnswer> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = await supabase.functions.invoke('book-lookup', { body: { search: text } });
  if (error) {
    // A 400 carries the message (too long, empty); anything else is the sites or the function.
    const ctx = (error as { context?: Response }).context;
    try {
      const body = ctx ? ((await ctx.json()) as SearchAnswer) : null;
      if (body?.message) return body;
    } catch {
      // no readable body
    }
    throw new Error('The book search didn’t answer. Try again, or add it by hand.');
  }
  return data as SearchAnswer;
}

/** A picked Open Library book's summary (search results don't carry it). */
export async function detailsFor(openLibraryId: string): Promise<{ summary: string | null; summarySource: string | null; subtitle: string | null }> {
  if (!supabase) return { summary: null, summarySource: null, subtitle: null };
  const { data, error } = await supabase.functions.invoke('book-lookup', { body: { details: { openLibraryId } } });
  if (error || !data) return { summary: null, summarySource: null, subtitle: null };
  return data as { summary: string | null; summarySource: string | null; subtitle: string | null };
}

export interface Added {
  book: string;
  label: string;
  actions: string[];
}

/** Adds the book; with `entry` (a book on a list, Discover Part 3b, 0057) it's added from Discover. */
export async function addBook(details: Record<string, unknown>, entry?: string): Promise<Added> {
  if (!supabase) throw new Error('Not connected to a database.');
  const { data, error } = entry ? await supabase.rpc('discover_add', { entry, details }) : await supabase.rpc('book_add', { details });
  if (error) throw new Error(error.message);
  booksChanged();
  return data as Added;
}

/** Undo an add: its actions last first (the read, the copy, then the add). */
export async function undoAdd(actions: string[]): Promise<void> {
  if (!supabase) return;
  for (const a of [...actions].reverse()) {
    const { error } = await supabase.rpc('undo_action', { action: a });
    if (error) throw new Error(error.message);
  }
  booksChanged();
}

/** "Already on your list as B0123." → 123, so Add a book can offer Open it. */
export const alreadyLabel = (message: string): number | null => {
  const m = message.match(/Already on your list as B0*(\d+)/);
  return m ? Number(m[1]) : null;
};

